import React, { useState } from 'react';
import CustomButton from '../../../components/CustomButton';
import { translate } from '../../../services/translate';
import ExcelJS from 'exceljs';
import { MonitoredPlatesDashboardInfo } from '../../../services/central-api/dashboard-summary';
import Toast from 'react-native-toast-message';
import { DashBoardParamList } from '../../../typings/Params';
import moment from 'moment';

interface Props {
    dashboardData: MonitoredPlatesDashboardInfo;
    filters: DashBoardParamList['DashboardPlateMonitoring'];
}

export default function ExportCsvButton({ dashboardData, filters }: Props) {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    function getPeriodMessage() {
        if (filters.fixedPeriod === 'select' && filters.beginIn && filters.endIn) {
            return `${moment(filters.beginIn).format('DD/MM')} - ${moment(filters.endIn).format('DD/MM')}`;
        } else if (filters.fixedPeriod === 'lastHour') {
            return translate('lastHour');
        } else if (filters.fixedPeriod === 'last12Hour') {
            return translate('last12Hour');
        } else if (filters.fixedPeriod === 'last24Hours') {
            return translate('last24Hours');
        } else if (filters.fixedPeriod === 'weekly') {
            return translate('last7days');
        } else if (filters.fixedPeriod === 'fortnightly') {
            return translate('last15days');
        } else if (filters.fixedPeriod === 'monthly') {
            return translate('last30days');
        }
    }

    async function generateCsv() {
        try {
            setIsLoading(true);
            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet(translate('plateMonitoring'));

            const chartData1 = [
                { title: translate('morning'), value: dashboardData.morningPlatesQty },
                { title: translate('afternoon'), value: dashboardData.afternoonPlatesQty },
                { title: translate('nighttime'), value: dashboardData.nightPlatesQty },
                { title: translate('earlyMorning'), value: dashboardData.earlyMorningPlatesQty },
            ];

            const chartData2 = dashboardData.rankingPlates.map(p => {
                return { title: p[0], value: p[1] };
            });

            sheet.getCell('A1').value = translate('period');
            if (filters.fixedPeriod !== 'select') {
                sheet.getCell('B1').value = getPeriodMessage();
                sheet.getCell('C1').value = translate('from');
            } else {
                sheet.getCell('B1').value = moment(filters.beginIn).format('DD/MM/YYYY HH:mm:00');
                sheet.getCell('C1').value = translate('to');
            }
            sheet.getCell('D1').value = moment(filters.endIn).format('DD/MM/YYYY HH:mm:00');

            sheet.getCell('F1').value = `${translate('lastUpdate')}: ${moment(dashboardData.lastCalculationTime).format('DD/MM/YYYY - HH:mm:00')}`;

            sheet.getCell('A3').value = translate('uniqueCapturedPlates');
            sheet.getCell('A4').value = dashboardData.uniquePlatesQty;

            const allCapturedPlates = (dashboardData.morningPlatesQty)
                + (dashboardData.afternoonPlatesQty)
                + (dashboardData.nightPlatesQty)
                + (dashboardData.earlyMorningPlatesQty);

            sheet.getCell('D3').value = translate('capturedPlatesInPeriod');
            sheet.getCell('D4').value = allCapturedPlates;

            sheet.getCell('A6').value = translate('capturedPlatesPerPeriod');
            for (let i = 0; i < chartData1.length; i++) {
                sheet.getCell(`A${7 + i}`).value = chartData1[i].title;
                sheet.getCell(`B${7 + i}`).value = chartData1[i].value;
            }

            sheet.getCell('D6').value = translate('topFivePlates');
            for (let i = 0; i < chartData2.length; i++) {
                sheet.getCell(`D${7 + i}`).value = chartData2[i].title;
                sheet.getCell(`E${7 + i}`).value = chartData2[i].value;
            }

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = 'dashboard.xlsx';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(objectUrl);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <CustomButton
            text={translate('exportCSV')}
            onPress={generateCsv}
            isLoading={isLoading}
        />
    );
}
